import {noop} from '@github-ui/noop'
import {createContext, type PropsWithChildren, use, useMemo, useState} from 'react'

export type ItemsContextProps = {
  /**
   * Whether any of the items in the ListView include an action bar. Will be used to ensure consistent
   * alignment of the ListItem's trailing content.
   */
  anyItemsWithActionBar: boolean
  /**
   * Register whether any ListItem includes an action bar or not.
   */
  setAnyItemsWithActionBar: (hasActionBar: boolean) => void
  /**
   * Whether any of the items in the ListView includes actions that will move into a dropdown menu when the screen isn't big enough to
   * fit them side by side.. Will be used to ensure consistent alignment of the ListItem's trailing content.
   */
  hasResizableActionsWithActionBar: boolean
  setHasResizableActionsWithActionBar: (hasActionBar: boolean) => void
}

const ItemsContext = createContext<ItemsContextProps>({
  anyItemsWithActionBar: false,
  setAnyItemsWithActionBar: noop,
  hasResizableActionsWithActionBar: false,
  setHasResizableActionsWithActionBar: noop,
})

export const ItemsProvider = ({children}: PropsWithChildren) => {
  const [anyItemsWithActionBar, setAnyItemsWithActionBar] = useState(false)
  const [hasResizableActionsWithActionBar, setHasResizableActionsWithActionBar] = useState(false)
  const contextProps = useMemo(
    () =>
      ({
        anyItemsWithActionBar,
        setAnyItemsWithActionBar,
        hasResizableActionsWithActionBar,
        setHasResizableActionsWithActionBar,
      }) satisfies ItemsContextProps,
    [anyItemsWithActionBar, hasResizableActionsWithActionBar],
  )
  return <ItemsContext value={contextProps}>{children}</ItemsContext>
}

ItemsProvider.displayName = 'ListViewItemsProvider'

export const useListViewItems = () => {
  return use(ItemsContext)
}

try{ ItemsContext.displayName ||= 'ItemsContext' } catch {}